import { makeStyles } from "tss-react/mui"
import { Alert, AlertTitle, Box, MenuItem, Stack, Typography } from "@mui/material"
import {
    FormikDropdown,
    FormikRichTextEditor,
    FormikSwitch,
    FormikTextField,
} from "../../../../../../shared/components/forms/Formik"
import React from "react"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"
import { ExternalLink } from "../../../../../../shared/components/links/ExternalLink"
import {
    LanguageCode,
    TermsAndConditionsFormValues,
    TermsAndConditionsHistory,
    TermsAndConditionsType,
} from "../termsAndConditionTypes"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../../shared/components/forms/StandardSidePanel"

const WHAT_SHOULD_I_INCLUDE_URL = "https://support.facilitynet.dk/hc/da/articles/17654349238802-Salgsbetingelser"
const DA_TEMPLATE_URL =
    "https://facilitynet.hosted-by-files.com/facilitynet-public/docs/gopay/templates/Template_Terms_and_conditions_GoPay_da.docx"
const EN_TEMPLATE_URL =
    "https://facilitynet.hosted-by-files.com/facilitynet-public/docs/gopay/templates/Template_Terms_and_conditions_GoPay_en.docx"

const useStyles = makeStyles()((theme) => ({
    dropdown: {
        width: "12rem",
    },
    historyContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
    },
    infoBox: {
        display: "flex",
        justifyContent: "space-between",
    },
    cardContentLink: {
        color: theme.palette.tailwind.sky[500],
        textDecoration: "none",
        transition: "all ease-in-out 0.3s",
        "&:hover": {
            color: `${theme.palette.tailwind.sky[800]}`,
        },
    },
}))

export function TermsAndConditionFormContent({
    history,
    version,
}: {
    history?: TermsAndConditionsHistory
    version?: number
}) {
    const { classes } = useStyles()

    return (
        <StandardForm>
            <StandardMainPanel>
                <Alert severity="info">
                    <Stack direction="column" spacing={2}>
                        <LocalizedStrict id="supplier-terms-and-conditions-alert-title">
                            <AlertTitle>Terms and conditions</AlertTitle>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-terms-and-conditions-alert-subtitle1">
                            <Typography>
                                It is your own responsibility to ensure that the terms and conditions comply with the
                                applicable requirements.
                            </Typography>
                        </LocalizedStrict>

                        <LocalizedStrict
                            id="supplier-terms-and-conditions-alert-subtitle2"
                            elems={{
                                externalLink: (
                                    <ExternalLink href={WHAT_SHOULD_I_INCLUDE_URL} className={classes.cardContentLink}>
                                        here
                                    </ExternalLink>
                                ),
                            }}
                        >
                            <Typography>
                                Read about Adyen's required T&C, which you must comply with.{" "}
                                <ExternalLink href={WHAT_SHOULD_I_INCLUDE_URL} className={classes.cardContentLink}>
                                    here
                                </ExternalLink>
                            </Typography>
                        </LocalizedStrict>

                        <LocalizedStrict
                            id="supplier-terms-and-conditions-alert-subtitle3"
                            elems={{
                                externalLinkDA: (
                                    <ExternalLink href={DA_TEMPLATE_URL} className={classes.cardContentLink}>
                                        Danish
                                    </ExternalLink>
                                ),
                                externalLinkEN: (
                                    <ExternalLink href={EN_TEMPLATE_URL} className={classes.cardContentLink}>
                                        English
                                    </ExternalLink>
                                ),
                            }}
                        >
                            <Typography>
                                Download our T&C template:
                                <ExternalLink href={DA_TEMPLATE_URL} className={classes.cardContentLink}>
                                    Danish
                                </ExternalLink>
                                |
                                <ExternalLink href={EN_TEMPLATE_URL} className={classes.cardContentLink}>
                                    English
                                </ExternalLink>
                            </Typography>
                        </LocalizedStrict>
                    </Stack>
                </Alert>
                <LocalizedStrict id="supplier-terms-and-conditions-type-label" attrs={{ label: true }}>
                    <FormikDropdown<TermsAndConditionsFormValues> name="type" className={classes.dropdown} label="Type">
                        <MenuItem value={TermsAndConditionsType.POS}>
                            <LocalizedStrict id="supplier-terms-and-conditions-dropdown-pos">
                                <span>POS</span>
                            </LocalizedStrict>
                        </MenuItem>
                        <MenuItem value={TermsAndConditionsType.ECOM}>
                            <LocalizedStrict id="supplier-terms-and-conditions-type-ecom">
                                <span>E-Commerce</span>
                            </LocalizedStrict>
                        </MenuItem>
                    </FormikDropdown>
                </LocalizedStrict>
                <LocalizedStrict id="supplier-terms-and-conditions-name-label" attrs={{ label: true }}>
                    <FormikTextField<TermsAndConditionsFormValues> name="callName" label="Name" fullWidth />
                </LocalizedStrict>
                <LocalizedStrict id="supplier-terms-and-conditions-language-label" attrs={{ label: true }}>
                    <FormikDropdown<TermsAndConditionsFormValues>
                        name="languageCode"
                        className={classes.dropdown}
                        label="Language"
                    >
                        <MenuItem value={LanguageCode.EN}>
                            <LocalizedStrict id="supplier-terms-and-conditions-dropdown-english">
                                <span>English</span>
                            </LocalizedStrict>
                        </MenuItem>
                        <MenuItem value={LanguageCode.DA}>
                            <LocalizedStrict id="supplier-terms-and-conditions-dropdown-danish">
                                <span>Danish</span>
                            </LocalizedStrict>
                        </MenuItem>
                    </FormikDropdown>
                </LocalizedStrict>
                <FormikRichTextEditor<TermsAndConditionsFormValues>
                    name="text"
                    placeholder="Body"
                    toolbar="undo redo blocks | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist | fullscreen"
                    helperText=""
                    onCharacterCountChange={() => {}}
                />
            </StandardMainPanel>
            <LocalizedStrict id="supplier-terms-and-conditions-aside-settings" attrs={{ title: true }}>
                <StandardSidePanel variant="collapsible" title="Settings">
                    <LocalizedStrict id="supplier-terms-and-conditions-aside-active">
                        <FormikSwitch<TermsAndConditionsFormValues> name="isActive">Active:</FormikSwitch>
                    </LocalizedStrict>
                </StandardSidePanel>
            </LocalizedStrict>
            {history && version && (
                <LocalizedStrict id="supplier-terms-and-conditions-aside-history">
                    <StandardSidePanel variant="collapsible" title="History">
                        <HistoryAccordion version={version} history={history} />
                    </StandardSidePanel>
                </LocalizedStrict>
            )}
        </StandardForm>
    )
}

function HistoryAccordion({ history, version }: { history: TermsAndConditionsHistory; version: number }) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const { created, updated } = history

    return (
        <Box className={classes.historyContainer}>
            <Box className={classes.infoBox}>
                <LocalizedStrict id="supplier-terms-and-conditions-aside-version">
                    <Typography>Version: </Typography>
                </LocalizedStrict>
                <Typography>{version}</Typography>
            </Box>
            <Box className={classes.infoBox}>
                <LocalizedStrict id="supplier-terms-and-conditions-aside-created">
                    <Typography>Created: </Typography>
                </LocalizedStrict>
                <Box>
                    <Typography align="right">
                        {dateTimeFactory.formatDate(dateTimeFactory.fromISO(created.timestamp))}
                    </Typography>
                    <Typography textAlign="right">{created.author ?? "-"}</Typography>
                </Box>
            </Box>
            {updated?.timestamp && (
                <Box className={classes.infoBox}>
                    <LocalizedStrict id="supplier-terms-and-conditions-aside-updated">
                        <Typography>Updated: </Typography>
                    </LocalizedStrict>
                    <Box>
                        <Typography align="right">
                            {dateTimeFactory.formatDate(dateTimeFactory.fromISO(updated.timestamp))}
                        </Typography>
                        <Typography textAlign="right">{updated.author ?? "-"}</Typography>
                    </Box>
                </Box>
            )}
        </Box>
    )
}
